import { motion } from "framer-motion";
import { ArrowRight, Users, Sprout, Leaf, Compass } from "lucide-react";

const PILLARS = [
  { key: "community", icon: Users, color: "text-primary", title: "Community-Directed", line: "Shaped by the people it serves, not handed down from above." },
  { key: "intergenerational", icon: Sprout, color: "text-bloom", title: "Intergenerational", line: "Elders and young ones learning side by side." },
  { key: "land", icon: Leaf, color: "text-leaf", title: "Land-Based", line: "Rooted in place, ecology and ancestral relationship." },
  { key: "purpose", icon: Compass, color: "text-gold", title: "Purpose-Led", line: "Guided by Nia — building collective goals together." },
];

const VISION = [
  { n: "01", title: "LifeSchoolHouse", line: "Neighbourhood hubs where skills and gifts are shared, barter-based and heart-led." },
  { n: "02", title: "Learning Village", line: "A gathering of hubs growing into a community-held village of learning and culture." },
  { n: "03", title: "Ecoversity", line: "A global movement where ecology, culture and learning become one — a worldwide web of land-connected learning, not bound to any single place." },
];

const reveal = (d = 0) => ({
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" }, transition: { duration: 0.7, delay: d },
});

const Intro = () => (
  <div className="grid gap-12 lg:grid-cols-12">
    <motion.div {...reveal()} className="lg:col-span-6">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Learning and Skills Exchanges</p>
      <h2 className="mt-6 font-display text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl" data-testid="exchanges-heading">
        Learning that belongs to <span className="text-primary">the community.</span>
      </h2>
    </motion.div>
    <motion.p {...reveal(0.1)} className="text-base leading-relaxed text-foreground/75 sm:text-lg lg:col-span-6 lg:pt-10" data-testid="exchanges-intro">
      Nia imagines learning that is community-directed, intergenerational, and non-institutional — where elders, children,
      artists and neighbours all teach and learn from one another. It honours the knowledge that already lives in our
      people, our stories, and our land.
    </motion.p>
  </div>
);

const Pillars = () => (
  <div className="mt-16 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
    {PILLARS.map((p, i) => (
      <motion.div key={p.key} {...reveal(i * 0.08)} className="bg-card p-10" data-testid={`exchanges-pillar-${p.key}`}>
        <p.icon className={`h-7 w-7 ${p.color}`} strokeWidth={1.5} />
        <h3 className="mt-6 font-display text-2xl font-bold text-foreground">{p.title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-foreground/70">{p.line}</p>
      </motion.div>
    ))}
  </div>
);

const LifeSchoolHouse = () => (
  <motion.div {...reveal()} className="mt-20 grid gap-10 bg-plum p-10 text-white sm:p-14 lg:grid-cols-12" data-testid="lifeschoolhouse-block">
    <div className="lg:col-span-5">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">A LifeSchoolHouse chapter</p>
      <h3 className="mt-5 font-display text-3xl font-bold leading-tight sm:text-4xl">A heart-centered, neighbourhood learning hub.</h3>
    </div>
    <div className="lg:col-span-7">
      <p className="text-base leading-relaxed text-white/85 sm:text-lg">
        Nia grows as part of the wider LifeSchoolHouse movement — a barter-based, heart-centered model of neighbourhood
        learning hubs where people share skills, gifts and time freely. It's learning returned to the hands of community.
      </p>
      <a href="https://lifeschoolhouse.com/" target="_blank" rel="noopener noreferrer" data-testid="lifeschoolhouse-link"
        className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-gold">
        Explore LifeSchoolHouse <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </a>
    </div>
  </motion.div>
);

const Vision = () => (
  <div className="mt-20">
    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">The bigger vision</p>
    <h3 className="mt-5 max-w-3xl font-display text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
      From a learning house to a living ecoversity.
    </h3>
    <div className="mt-12 grid gap-px border border-border bg-border md:grid-cols-3">
      {VISION.map((v, i) => (
        <motion.div key={v.n} {...reveal(i * 0.1)} className="bg-card p-10" data-testid={`vision-step-${v.n}`}>
          <span className="font-display text-5xl font-extrabold text-gold">{v.n}</span>
          <h4 className="mt-6 font-display text-2xl font-bold text-foreground">{v.title}</h4>
          <p className="mt-3 text-sm leading-relaxed text-foreground/70">{v.line}</p>
        </motion.div>
      ))}
    </div>
    <p className="mt-10 max-w-2xl text-base leading-relaxed text-foreground/75">
      This vision is still taking root. We're sharing the dream openly and invite you to help shape what it becomes.
    </p>
  </div>
);

export const LearningExchanges = () => (
  <section id="exchanges" className="border-t border-border bg-muted/50 py-24 lg:py-32" data-testid="exchanges-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Intro />
      <Pillars />
      <LifeSchoolHouse />
      <Vision />
    </div>
  </section>
);
